import { useEffect, useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Mail, Check, X } from 'lucide-react'
import { api } from '../lib/api'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { PasswordField } from '../components/ui/PasswordField'

/**
 * „Mailul meu" — fiecare își conectează singur căsuța.
 *
 * Alexandru: „putem adăuga un meniu să apară la fiecare utilizator buton de conectare email? Și
 * să-și introducă fiecare parola, care să fie apoi criptată?".
 *
 * Nevoia a ieșit la iveală când Adela și Mădălin aveau Inboxul gol. Nu erau mesajele lor
 * ascunse — nimeni nu se conectase vreodată la căsuțele lor. Singura conectată era `contact@`,
 * pusă de un admin, iar ecranul de adăugare e în Setări, unde ei n-au acces.
 *
 * Parola nu se întoarce niciodată de la server. Câmpul e mereu gol, iar gol la salvare înseamnă
 * „păstreaz-o pe cea de acum".
 */
interface Casuta {
  id: number
  email: string
  imapHost: string
  imapPort: number
  imapUser: string
  active: boolean
  scanTasks: boolean
  parolaSetata: boolean
  ultimaCitire: string | null
  /** Serverul a refuzat parola: căsuța nu se mai citește până o salvezi din nou. */
  eroare: string | null
}

function mesaj(e: unknown): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response
  return r?.data?.error?.message ?? 'A apărut o eroare'
}

function clipa(iso: string | null): string {
  if (!iso) return 'niciodată'
  return new Date(iso).toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' })
}

export function CasutaMeaPage({
  inMail = false,
  laSalvat,
}: {
  /** Deschisă în pagina „Mail", când căsuța lipsește sau parola a fost refuzată. */
  inMail?: boolean
  laSalvat?: () => void
} = {}) {
  const qc = useQueryClient()
  const [host, setHost] = useState('mail.boost4life.ro')
  const [port, setPort] = useState(993)
  const [utilizator, setUtilizator] = useState('')
  const [parola, setParola] = useState('')
  const [scanTasks, setScanTasks] = useState(false)
  const [rezultatTest, setRezultatTest] = useState<{ ok: boolean; text: string } | null>(null)
  /*
    Parola se citeste si din camp, nu doar din stare (Raluca, 20.10.2026): pusa de managerul de
    parole sau lipita fara clic in camp, aparea scrisa, dar starea ramanea goala — iar butoanele
    stateau gri, fara nicio explicatie.
  */
  const parolaRef = useRef<HTMLInputElement>(null)
  const parolaScrisa = () => parolaRef.current?.value || parola

  const q = useQuery<{ casuta: Casuta | null; emailContului: string }>({
    queryKey: ['casuta-mea'],
    queryFn: async () => (await api.get('/settings/casuta-mea')).data,
  })

  // Se completează o singură dată, când vin datele: altfel ar șterge ce scrie omul acum.
  useEffect(() => {
    const c = q.data?.casuta
    if (!c) {
      if (q.data?.emailContului && !utilizator) setUtilizator(q.data.emailContului)
      return
    }
    setHost(c.imapHost)
    setPort(c.imapPort)
    setUtilizator(c.imapUser)
    setScanTasks(c.scanTasks)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.casuta?.id])

  const testeaza = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ ok: boolean; mesaje?: number; eroare?: string }>(
          '/settings/casuta-mea/test',
          { imapHost: host, imapPort: port, imapUser: utilizator, imapPassword: parolaScrisa() },
          { timeout: 45_000 },
        )
      ).data,
    onSuccess: (r) =>
      setRezultatTest(
        r.ok
          ? { ok: true, text: `Conectat. ${r.mesaje ?? 0} mesaje în căsuță.` }
          : { ok: false, text: r.eroare ?? 'Nu s-a putut conecta.' },
      ),
    onError: (e) => setRezultatTest({ ok: false, text: mesaj(e) }),
  })

  const salveaza = useMutation({
    mutationFn: async () =>
      (
        await api.put('/settings/casuta-mea', {
          imapHost: host,
          imapPort: port,
          imapUser: utilizator,
          ...(parolaScrisa().trim() ? { imapPassword: parolaScrisa() } : {}),
          active: true,
          scanTasks,
        })
      ).data,
    onSuccess: () => {
      toast.success('Căsuța e conectată. O găsești în pagina „Mail”.')
      setParola('')
      if (parolaRef.current) parolaRef.current.value = ''
      setRezultatTest(null)
      void qc.invalidateQueries({ queryKey: ['casuta-mea'] })
      void qc.invalidateQueries({ queryKey: ['webmail-stare'] })
      laSalvat?.()
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const deconecteaza = useMutation({
    mutationFn: async () => api.delete('/settings/casuta-mea'),
    onSuccess: () => {
      toast.success('Căsuța a fost deconectată. Parola a fost ștearsă.')
      void qc.invalidateQueries({ queryKey: ['casuta-mea'] })
      void qc.invalidateQueries({ queryKey: ['webmail-stare'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const c = q.data?.casuta
  /** Ce lipseste, spus pe ecran — butoanele nu mai stau gri fara explicatie. */
  const lipsa = () =>
    !host.trim()
      ? 'Completează serverul IMAP (mail.boost4life.ro).'
      : !utilizator.trim()
        ? 'Completează utilizatorul — adresa ta de email.'
        : !parolaScrisa().trim() && !c?.parolaSetata
          ? 'Scrie parola căsuței de mail (cea de la webmail, nu cea de la CRM).'
          : null
  const daca = (fa: () => void) => () => {
    const l = lipsa()
    if (l) setRezultatTest({ ok: false, text: l })
    else fa()
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold">{inMail ? 'Conectează-ți căsuța' : 'Mailul meu'}</h1>
        <p className="mt-0.5 text-sm text-b4l-text-muted">
          Conectează-ți căsuța ca să-ți folosești mailul în pagina „Mail”, din CRM. Parola se
          criptează la salvare și nu mai poate fi citită de nimeni — nici de administratori.
        </p>
      </div>

      <Card>
        <div className="space-y-3 p-4 text-sm">
          {c?.eroare ? (
            <p className="flex flex-wrap items-center gap-2 rounded-lg bg-b4l-red-light p-2 text-b4l-red-dark">
              <X size={15} />
              <strong>{c.email}</strong> nu se mai citește: {c.eroare} Am oprit încercările, ca
              serverul de mail să nu blocheze conexiunea.
            </p>
          ) : c ? (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-b4l-green-light p-2 text-b4l-green-dark">
              <Mail size={15} />
              <strong>{c.email}</strong> e conectată.
              <span className="text-xs">Ultima citire: {clipa(c.ultimaCitire)}</span>
            </div>
          ) : (
            <p className="rounded-lg bg-b4l-amber-light p-2 text-b4l-amber-dark">
              Nu ai nicio căsuță conectată, deci pagina „Mail” nu are ce să-ți arate.
            </p>
          )}

          <label className="block">
            <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Adresa</span>
            <input
              value={q.data?.emailContului ?? ''}
              disabled
              className="w-full rounded border border-b4l-border bg-b4l-bg px-3 py-2 text-b4l-text-muted"
            />
            <span className="mt-0.5 block text-[11px] text-b4l-text-muted">
              E adresa contului tău și nu se poate schimba de aici — altfel cineva și-ar putea lega
              contul la căsuța altcuiva.
            </span>
          </label>

          <div className="flex flex-wrap gap-3">
            <label className="block flex-1">
              <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Server IMAP</span>
              <input
                value={host}
                onChange={(e) => setHost(e.target.value)}
                className="w-full rounded border border-b4l-border px-3 py-2"
              />
            </label>
            <label className="block w-24">
              <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Port</span>
              <input
                type="number"
                value={port}
                onChange={(e) => setPort(Number(e.target.value) || 993)}
                className="w-full rounded border border-b4l-border px-3 py-2"
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Utilizator</span>
            <input
              value={utilizator}
              onChange={(e) => setUtilizator(e.target.value)}
              className="w-full rounded border border-b4l-border px-3 py-2"
            />
          </label>

          <div>
            <PasswordField
              ref={parolaRef}
              label={c?.parolaSetata ? 'Parolă nouă (lasă gol ca s-o păstrezi)' : 'Parolă'}
              value={parola}
              onChange={(e) => setParola(e.target.value)}
              onInput={(e) => setParola(e.currentTarget.value)}
              autoComplete="new-password"
            />
            {/* Un spațiu prins la copiere schimbă parola: se salvează exact cum e scrisă. */}
            {parolaScrisa() !== parolaScrisa().trim() && (
              <p className="mt-1 text-[11px] text-b4l-amber-dark">
                Parola începe sau se termină cu un spațiu. Se salvează exact așa — dacă nu e
                intenționat, șterge-l.
              </p>
            )}
          </div>

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={scanTasks}
              onChange={(e) => setScanTasks(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Creează task-uri automat din mesajele mele
              <span className="block text-[11px] text-b4l-text-muted">
                Oprit implicit. Cu el pornit, fiecare mail care pare a fi o treabă ajunge la „De
                preluat”, în departamentul de care ține.
              </span>
            </span>
          </label>

          {rezultatTest && (
            <p
              className={`flex items-start gap-1.5 rounded-lg p-2 text-xs ${
                rezultatTest.ok
                  ? 'bg-b4l-green-light text-b4l-green-dark'
                  : 'bg-b4l-red-light text-b4l-red-dark'
              }`}
            >
              {rezultatTest.ok ? <Check size={14} /> : <X size={14} />}
              {rezultatTest.text}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            {c ? (
              <button
                onClick={() => {
                  if (window.confirm('Deconectezi căsuța? Parola se șterge din CRM.')) {
                    deconecteaza.mutate()
                  }
                }}
                className="text-xs text-b4l-red hover:underline"
              >
                Deconectează căsuța
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={daca(() => testeaza.mutate())}
                disabled={testeaza.isPending}
              >
                {testeaza.isPending ? 'Verific…' : 'Testează conexiunea'}
              </Button>
              <Button onClick={daca(() => salveaza.mutate())} disabled={salveaza.isPending}>
                {salveaza.isPending ? 'Se salvează…' : 'Conectează'}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
